import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import logoImage from '../../assets/images/logo.png';
import { 
  AppBar, 
  Box,
  Container,
  CssBaseline, 
  IconButton, 
  Toolbar, 
  Typography, 
  Snackbar,
  Alert,
  useTheme,
  Link,
  useMediaQuery
} from '@mui/material';
import Footer from '../common/Footer';
import {
  Settings as SettingsIcon,
  Help as HelpIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import { hideNotification } from '../../store/slices/uiSlice';
import EnvironmentBadge from '../common/EnvironmentBadge';

const RoleplayLayout: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { currentUser: user } = useSelector((state: RootState) => state.user);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const appBarRef = useRef<HTMLDivElement>(null);
  const [appBarHeight, setAppBarHeight] = useState(60); // デフォルト値
  
  // デバッグ用：ユーザー情報をコンソールに出力
  // React.useEffect(() => {
  //   console.log('Current user:', user);
  // }, [user]);
  const { notification } = useSelector((state: RootState) => state.ui);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleNotificationClose = () => {
    dispatch(hideNotification());
  };

  const location = useLocation();
  const { currentScenario } = useSelector((state: RootState) => state.scenario);
  
  // パスに基づいてツールバータイトルを設定
  const getToolTitle = () => {
    const path = location.pathname;
    
    if (path.startsWith('/scenarios')) {
      return 'TOP';
    } else if (path.startsWith('/roleplay-settings')) {
      return 'オリエンテーション';
    } else if (path.startsWith('/roleplay') && currentScenario) {
      return currentScenario.title;
    } else if (path.startsWith('/evaluation')) {
      return 'AIフィードバック';
    } else if (path.startsWith('/help')) {
      return 'ヘルプ・お問い合わせ';
    }
    
    return '';
  };

  const toolTitle = getToolTitle();

  // AppBarの高さを動的に測定
  useEffect(() => {
    if (isSmallScreen && appBarRef.current) {
      const updateAppBarHeight = () => {
        const height = appBarRef.current?.offsetHeight || 60;
        setAppBarHeight(height);
      };

      // 初回測定
      updateAppBarHeight();

      // ResizeObserverでAppBarの高さ変化を監視
      const resizeObserver = new ResizeObserver(() => {
        updateAppBarHeight();
      });

      resizeObserver.observe(appBarRef.current);

      // クリーンアップ
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [isSmallScreen, toolTitle]); // toolTitleが変わった時も再測定

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      bgcolor: theme.palette.background.paper,
      justifyContent: 'space-between',
    }}>
      <CssBaseline />
      <Box
        sx={{
          position: 'fixed',
          top: isSmallScreen ? 12 : 12,
          right: 16,
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
      >
        <EnvironmentBadge compact={isSmallScreen} />
      </Box>
      { isSmallScreen && (
      <AppBar
        ref={appBarRef}
        position="fixed"
        sx={{ width: '100%' }}
      >
        <Toolbar sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme.palette.smartSkillTalk.linkBlue,
          padding: '13px 40px',
        }}>
          <IconButton 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ padding: 0 }}
          >
            <HomeIcon sx={{ 
              width: '26px', 
              height: '26px', 
              color: '#FFFFFF' 
            }} />
          </IconButton>
          <Typography sx={{
            fontSize: '20px',
            fontWeight: 400,
            lineHeight: '1.5em',
            color: '#FFFFFF'
          }}>
            { toolTitle || 'none' }
          </Typography>
          {user?.userRole === "ADMIN" ? (
            <IconButton 
              color="inherit"
              onClick={() => navigate('/admin')}
              sx={{ padding: 0 }}
            >
              <SettingsIcon sx={{ 
                width: '26px', 
                height: '26px', 
                color: '#FFFFFF' 
              }} />
            </IconButton>
          ): (
            <IconButton 
              sx={{ padding: 0 }}
            >
              <SettingsIcon sx={{ 
                width: '0px', 
                height: '0px', 
                color: theme.palette.smartSkillTalk.linkBlue,
              }} />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      )}
      <Container sx={{ padding: '0px' }}>
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: '100%',
          padding: '0px',
          mt: isSmallScreen ? `${appBarHeight}px`: '0px', // AppBarの実際の高さに基づく動的マージン
        }}
      >
        <Outlet />
      </Box>
      </Container>
      <Snackbar 
        open={notification.show} 
        autoHideDuration={6000} 
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      {/* <Footer /> */}
    </Box>
  );
};

export default RoleplayLayout;
